import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { logAction, getCurrentAdmin } from '../services/logger';
import { StatusBadge } from '../components/StatusBadge';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Camera, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

export function StoriesPage() {
  const [bars, setBars] = useState<any[]>([]);
  const [matches, setMatches] = useState<any[]>([]);
  const [barId, setBarId] = useState('');
  const [matchId, setMatchId] = useState('');
  const [note, setNote] = useState('');
  const [autoConfirm, setAutoConfirm] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState<any[]>([]);

  useEffect(() => {
    loadSelectors();
    loadConfirmed();
  }, []);

  const loadSelectors = async () => {
    const today = new Date().toISOString().split('T')[0];
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const [barsRes, matchesRes] = await Promise.all([
      supabase.from('bars').select('id, name').order('name'),
      supabase.from('matches')
        .select('id, home_team, away_team, match_time, match_date')
        .gte('match_date', today)
        .lte('match_date', tomorrow.toISOString().split('T')[0])
        .order('match_date')
        .order('match_time'),
    ]);

    setBars(barsRes.data || []);
    setMatches(matchesRes.data || []);
  };

  const loadConfirmed = async () => {
    const today = new Date().toISOString().split('T')[0];
    const { data } = await supabase
      .from('diffusions')
      .select('id, status, confirmed_at, confirmed_by, bars(name), matches(home_team, away_team, match_time, match_date)')
      .eq('status', 'confirmed')
      .limit(50);

    const filtered = (data || []).filter((d: any) => d.matches?.match_date === today);
    setConfirmed(filtered);
  };

  const handleSubmit = async () => {
    if (!barId || !matchId) { toast.error('Sélectionne un bar et un match'); return; }
    setSaving(true);

    const admin = getCurrentAdmin();
    const bar = bars.find(b => b.id === barId);
    const match = matches.find((m: any) => m.id === matchId);
    const status = autoConfirm ? 'confirmed' : 'probable';

    try {
      // Check if diffusion exists
      const { data: existing } = await supabase
        .from('diffusions')
        .select('id')
        .eq('bar_id', barId)
        .eq('match_id', matchId)
        .limit(1);

      if (existing && existing.length > 0) {
        await supabase
          .from('diffusions')
          .update({ status, confirmed_at: new Date().toISOString(), confirmed_by: 'admin' })
          .eq('id', existing[0].id);
      } else {
        await supabase
          .from('diffusions')
          .insert({ bar_id: barId, match_id: matchId, status, confirmed_at: new Date().toISOString(), confirmed_by: 'admin' });
      }

      const matchName = `${match?.home_team} vs ${match?.away_team}`;
      await logAction('confirm_diffusion', 'diffusion', existing?.[0]?.id || null,
        null, { status, note },
        `${admin?.name} a confirmé via story la diffusion de ${matchName} au ${bar?.name}${note ? ' — ' + note : ''}`
      );

      toast.success('Diffusion confirmée via story');
      setBarId('');
      setMatchId('');
      setNote('');
      loadConfirmed();
    } catch (e: any) {
      toast.error('Erreur: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="card">
        <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Camera size={18} style={{ color: 'var(--gold)' }} />
          Confirmation via Story Instagram
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginTop: '16px' }}>
          <div className="form-field">
            <label>Bar</label>
            <select value={barId} onChange={e => setBarId(e.target.value)} className="filter-select" style={{ width: '100%' }}>
              <option value="">Choisir un bar...</option>
              {bars.map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label>Match</label>
            <select value={matchId} onChange={e => setMatchId(e.target.value)} className="filter-select" style={{ width: '100%' }}>
              <option value="">Choisir un match...</option>
              {matches.map((m: any) => (
                <option key={m.id} value={m.id}>{m.home_team} vs {m.away_team} ({m.match_time})</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-field">
          <label>Note admin</label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Story vue à 15h32, logo Canal+, ambiance match..."
            rows={2}
            style={{ resize: 'vertical' }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <input
            type="checkbox"
            id="autoConfirm"
            checked={autoConfirm}
            onChange={e => setAutoConfirm(e.target.checked)}
          />
          <label htmlFor="autoConfirm" style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0 }}>
            Marquer comme Confirmé directement
          </label>
        </div>

        <button className="btn btn-green" onClick={handleSubmit} disabled={saving}>
          <CheckCircle size={16} />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>

      <div className="card" style={{ marginTop: '20px' }}>
        <h3 className="card-title">Confirmations du jour</h3>
        {confirmed.length === 0 ? (
          <p className="muted">Aucune confirmation aujourd'hui</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Bar</th>
                <th>Match</th>
                <th>Statut</th>
                <th>Confirmé par</th>
                <th>Heure</th>
              </tr>
            </thead>
            <tbody>
              {confirmed.map((d: any) => (
                <tr key={d.id}>
                  <td style={{ fontWeight: 600 }}>{d.bars?.name}</td>
                  <td>{d.matches?.home_team} vs {d.matches?.away_team}</td>
                  <td><StatusBadge status={d.status} /></td>
                  <td style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{d.confirmed_by || '-'}</td>
                  <td style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    {d.confirmed_at ? format(new Date(d.confirmed_at), 'HH\'h\'mm', { locale: fr }) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
